import type {
  ToothBasisPlaneRef,
  ToothBasisSeriesName,
  ToothMassCentroidMm,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type {
  ToothAxialLevels,
  ToothExtentsProfiles,
  ToothRootAxis,
  ToothRootRangeMm,
} from '../config';
import {
  DEFAULT_CARD_AXIAL_CROWN_INSET_MM,
  DEFAULT_CARD_AXIAL_ROOT_INSET_MM,
  DEFAULT_CARD_AXIAL_SHORT_CROWN_RATIO,
  DEFAULT_CARD_AXIAL_SHORT_ROOT_RATIO,
  DEFAULT_CARD_CROSS_SECTION_PAIR_SPREAD_MM,
  type ToothSliceSeriesOffsets,
} from '../config';

import { resolveToothAxialLevels } from './resolveToothAxialLevels';
import { resolveToothRootCentreMm } from './resolveToothRootPositions';

const pickIndexes = ({
  axialLevels,
  centroidMm,
  offsetsMm,
  rootCentreMm,
  seriesName,
}: {
  axialLevels: ToothAxialLevels | null;
  centroidMm: number | null;
  offsetsMm: number[];
  rootCentreMm: number | null;
  seriesName: ToothBasisSeriesName;
}) => {
  const nearestIndex = (targetMm: number) =>
    offsetsMm.reduce(
      (closest, offsetMm, index) =>
        Math.abs(offsetMm - targetMm) < Math.abs(offsetsMm[closest] - targetMm)
          ? index
          : closest,
      0,
    );

  if (seriesName === 'axial') {
    if (axialLevels) {
      return [
        nearestIndex(axialLevels.crownMm),
        nearestIndex(axialLevels.coronalRootMm),
        nearestIndex(axialLevels.rootMm),
      ];
    }

    const firstMm = offsetsMm[0];
    const lastMm = offsetsMm[offsetsMm.length - 1];
    const insetCrownMm = firstMm + DEFAULT_CARD_AXIAL_CROWN_INSET_MM;
    const insetRootMm = lastMm - DEFAULT_CARD_AXIAL_ROOT_INSET_MM;
    const spanMm = lastMm - firstMm;
    const [crownMm, rootMm] =
      insetCrownMm < insetRootMm
        ? [insetCrownMm, insetRootMm]
        : [
            firstMm + spanMm * DEFAULT_CARD_AXIAL_SHORT_CROWN_RATIO,
            firstMm + spanMm * DEFAULT_CARD_AXIAL_SHORT_ROOT_RATIO,
          ];

    return [
      nearestIndex(crownMm),
      nearestIndex((crownMm + rootMm) / 2),
      nearestIndex(rootMm),
    ];
  }

  const centreMm =
    rootCentreMm ??
    centroidMm ??
    (offsetsMm[0] + offsetsMm[offsetsMm.length - 1]) / 2;

  if (seriesName === 'frontal') {
    return [nearestIndex(centreMm)];
  }

  return [
    nearestIndex(centreMm - DEFAULT_CARD_CROSS_SECTION_PAIR_SPREAD_MM),
    nearestIndex(centreMm + DEFAULT_CARD_CROSS_SECTION_PAIR_SPREAD_MM),
  ];
};

export const makeDefaultToothSlicePlanes = ({
  massCentroidMm,
  profiles = null,
  series,
}: {
  massCentroidMm: ToothMassCentroidMm | null;
  series: ToothSliceSeriesOffsets[];
  profiles?: ToothExtentsProfiles | null;
}): ToothBasisPlaneRef[] => {
  const centroidBySeries: Record<ToothBasisSeriesName, number | null> = {
    axial: null,
    frontal: massCentroidMm?.y ?? null,
    sagittal: massCentroidMm?.x ?? null,
  };

  const axialOffsetsMm =
    series.find((entry) => entry.name === 'axial')?.offsetsMm ?? [];
  const axialLevels = resolveToothAxialLevels({ axialOffsetsMm, profiles });

  const rangeOf = (seriesName: ToothBasisSeriesName) => {
    const offsets = series.find(
      (entry) => entry.name === seriesName,
    )?.offsetsMm;

    return offsets?.length
      ? { max: offsets[offsets.length - 1], min: offsets[0] }
      : null;
  };

  const rangesMm: Record<ToothRootAxis, ToothRootRangeMm> = {
    md: rangeOf('sagittal'),
    vo: rangeOf('frontal'),
  };

  const rootCentreBySeries: Record<ToothBasisSeriesName, number | null> = {
    axial: null,
    frontal: resolveToothRootCentreMm({
      axialLevels,
      axis: 'vo',
      profiles,
      rangeMm: rangesMm.vo,
    }),
    sagittal: resolveToothRootCentreMm({
      axialLevels,
      axis: 'md',
      profiles,
      rangeMm: rangesMm.md,
    }),
  };

  const planesOf = (seriesName: ToothBasisSeriesName) => {
    const offsetsMm =
      series.find((entry) => entry.name === seriesName)?.offsetsMm ?? [];

    if (!offsetsMm.length) {
      return [];
    }

    const clamp = (index: number) =>
      Math.min(Math.max(index, 0), offsetsMm.length - 1);

    return [
      ...new Set(
        pickIndexes({
          axialLevels,
          centroidMm: centroidBySeries[seriesName],
          offsetsMm,
          rootCentreMm: rootCentreBySeries[seriesName],
          seriesName,
        }).map(clamp),
      ),
    ]
      .sort((left, right) => left - right)
      .map((index) => ({ offsetMm: offsetsMm[index], seriesName }));
  };

  return [
    ...planesOf('axial'),
    ...planesOf('frontal'),
    ...planesOf('sagittal'),
  ];
};
